import { Controller } from "@hotwired/stimulus"

// Types text behind a block cursor.
//
// Optionally types `initialText` first, pauses, deletes back to the shared
// prefix and types the real text: the "correction" upstream shows off. The
// timing constants are upstream's. Under reduced motion the final text is
// shown at once with a still cursor.
const FRAME = 1000 / 60
const TYPE_CPS = 30 * (85 / 75)
const DELETE_CPS = 24 * (85 / 75)
const CORRECTION_PAUSE = 350 / (85 / 75)
const SCRAMBLE = [">", "*", "=", "&", "+", "-", "%", "^", "_"]

export default class extends Controller {
  static targets = ["text", "cursor"]
  static values = {
    text: String,
    initialText: String,
    delay: { type: Number, default: 500 },
    waitForPageLoad: { type: Boolean, default: true }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !this.textValue) {
      this.render({ text: this.textValue, phase: "complete" })
      return
    }
    this.render({ text: "", phase: "initial", hideCursor: true })
    if (this.waitForPageLoadValue && document.readyState !== "complete") {
      this.loadHandler = () => this.startAfterDelay()
      window.addEventListener("load", this.loadHandler, { once: true })
    } else {
      this.startAfterDelay()
    }
  }

  disconnect() {
    clearTimeout(this.timer)
    cancelAnimationFrame(this.frame)
    if (this.loadHandler) window.removeEventListener("load", this.loadHandler)
  }

  startAfterDelay() {
    this.timer = setTimeout(() => this.start(), this.delayValue)
  }

  start() {
    const text = this.textValue
    const initial = this.initialTextValue && this.initialTextValue !== text ? this.initialTextValue : null
    let prefix = 0
    if (initial) {
      while (prefix < initial.length && prefix < text.length && initial[prefix] === text[prefix]) prefix += 1
    }
    const correcting = initial !== null && prefix < initial.length
    const first = correcting ? initial : text
    const deleted = correcting ? first.length - prefix : 0

    const firstTypeEnd = (first.length / TYPE_CPS) * 1000
    const correctionStart = firstTypeEnd + (correcting ? CORRECTION_PAUSE : 0)
    const secondTypeStart = correctionStart + (deleted / DELETE_CPS) * 1000
    const secondTypeEnd = secondTypeStart + (correcting ? ((text.length - prefix) / TYPE_CPS) * 1000 : 0)
    const complete = secondTypeEnd
    const correctionCursorAt = correctionStart - FRAME
    const finalCursorAt = complete - 6 * FRAME

    const startedAt = performance.now()
    let previous = ""
    const tick = now => {
      const elapsed = now - startedAt
      let visible = ""
      if (elapsed > 0 && elapsed < firstTypeEnd) {
        visible = scramble(first, Math.floor((elapsed / 1000) * TYPE_CPS))
      } else if (correcting && elapsed < correctionStart) {
        visible = first
      } else if (correcting && elapsed < secondTypeStart) {
        const gone = Math.floor(((elapsed - correctionStart) / 1000) * DELETE_CPS)
        visible = first.slice(0, first.length - gone)
      } else if (correcting && elapsed < complete) {
        const typed = Math.min(text.length, prefix + Math.floor(((elapsed - secondTypeStart) / 1000) * TYPE_CPS))
        visible = text.slice(0, typed)
      } else if (elapsed >= complete) {
        visible = text
      }

      let phase = "initial"
      if (correcting && elapsed >= correctionCursorAt) phase = "correction"
      if (elapsed >= finalCursorAt) phase = "final"
      if (elapsed >= complete) phase = "complete"

      const key = `${visible}|${phase}`
      if (key !== previous) {
        previous = key
        this.render({ text: visible, phase, hideCursor: visible.length === 0 })
      }
      if (elapsed < complete) this.frame = requestAnimationFrame(tick)
    }
    this.frame = requestAnimationFrame(tick)
  }

  render({ text, phase, hideCursor = false }) {
    this.textTarget.textContent = text
    const cursor = this.cursorTarget
    cursor.hidden = hideCursor
    cursor.className = `TextCursorAnimation__cursor TextCursorAnimation__cursor--animated TextCursorAnimation__cursor--${phase}`
  }
}

// While typing, the newest character is followed by a symbol that changes
// every frame: the "scramble" effect. Whitespace is passed through.
function scramble(source, count) {
  let result = ""
  let visibleIndex = 0
  for (let i = 0; i < count; i += 1) {
    const character = source[i]
    result += character
    if (/\s/.test(character)) continue
    visibleIndex += 1
    if (i === count - 1) result += SCRAMBLE[(visibleIndex - 1) % SCRAMBLE.length]
  }
  return result
}
